import { useEffect, useState, useMemo } from 'react';
import { Map as GoogleMap, Marker, useMap, useApiIsLoaded } from '@vis.gl/react-google-maps';
import { useNavigate } from 'react-router-dom';
import { themeClasses, OptimizedImage, useTheme, getMapStyles } from '@member-portal/ui';
import { Hotel } from './HotelCard';
import type { CollectionListSnapshot } from '@/types/collectionList';

interface HotelsMapProps {
  hotels: Hotel[];
  className?: string;
  collectionListSnapshot?: CollectionListSnapshot;
}

// Component to handle map bounds fitting
function MapBoundsHandler({ hotels }: { hotels: Hotel[] }) {
  const map = useMap();

  useEffect(() => {
    if (!map || hotels.length === 0) return;

    const hotelsWithCoords = hotels.filter(h => h.latitude && h.longitude);
    if (hotelsWithCoords.length === 0) return;

    // Wait for google maps to be available
    if (typeof google === 'undefined' || !google.maps) return;

    const bounds = new google.maps.LatLngBounds();
    hotelsWithCoords.forEach(hotel => {
      bounds.extend({ lat: hotel.latitude!, lng: hotel.longitude! });
    });

    // Fit bounds with padding
    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });

    // Limit max zoom when fitting bounds
    const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
      const zoom = map.getZoom();
      if (zoom && zoom > 12) {
        map.setZoom(12);
      }
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [map, hotels]);

  return null;
}

function HotelsMap({ hotels, className = '', collectionListSnapshot }: HotelsMapProps) {
  const apiIsLoaded = useApiIsLoaded();
  const navigate = useNavigate();
  const { isLightTheme } = useTheme();
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [mapActive, setMapActive] = useState(false);

  // Filter hotels with valid coordinates
  const hotelsWithCoords = useMemo(() => {
    return hotels.filter(h => h.latitude && h.longitude);
  }, [hotels]);

  // Calculate initial center (default to US if no hotels)
  const initialCenter = useMemo(() => {
    if (hotelsWithCoords.length === 0) {
      return { lat: 39.8283, lng: -98.5795 }; // Center of US
    }
    const avgLat = hotelsWithCoords.reduce((sum, h) => sum + h.latitude!, 0) / hotelsWithCoords.length;
    const avgLng = hotelsWithCoords.reduce((sum, h) => sum + h.longitude!, 0) / hotelsWithCoords.length;
    return { lat: avgLat, lng: avgLng };
  }, [hotelsWithCoords]);

  // Generate styled marker icon
  const getMarkerIcon = (isSelected: boolean): google.maps.Icon | undefined => {
    if (!apiIsLoaded || typeof window === 'undefined' || !window.google?.maps) return undefined;

    const size = isSelected ? 40 : 32;
    const fillColor = isSelected
      ? (isLightTheme ? '#000000' : '#ffffff')
      : (isLightTheme ? '#666666' : '#888888');
    const strokeColor = isLightTheme ? '#333333' : '#000000';

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none"
             xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13
                   c0-3.87-3.13-7-7-7z"
                fill="${fillColor}" stroke="${strokeColor}" stroke-width="1.5"/>
          <circle cx="12" cy="9" r="2.5" fill="${strokeColor}" opacity="0.8"/>
        </svg>
      `)}`,
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size / 2, size)
    };
  };

  const handleMarkerClick = (hotel: Hotel) => {
    setSelectedHotel(hotel);
  };

  const handleInfoCardClick = () => {
    if (selectedHotel) {
      navigate(`/collection/${selectedHotel.id}`, {
        state: {
          hotel: selectedHotel,
          ...(collectionListSnapshot ? { collectionList: collectionListSnapshot } : {}),
        },
      });
    }
  };

  const formatLocation = (hotel: Hotel) => {
    const parts = [hotel.city, hotel.state, hotel.country].filter(Boolean);
    return parts.join(', ');
  };

  const handleMapClick = () => {
    if (!mapActive) {
      setMapActive(true);
    } else {
      setSelectedHotel(null);
    }
  };

  return (
    <div className={`relative w-full h-full ${className}`}>
      {/* Click-to-activate overlay */}
      {!mapActive && (
        <div
          className={`absolute inset-0 z-10 flex items-center justify-center cursor-pointer ${isLightTheme ? 'bg-white/30' : 'bg-black/30'}`}
          onClick={() => setMapActive(true)}
        >
          <div className="bg-theme-bg text-theme-text px-4 py-2 border border-theme-border flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z" clipRule="evenodd" />
            </svg>
            <span className={themeClasses.text.mutedSmall}>Click to interact</span>
          </div>
        </div>
      )}

      <GoogleMap
        defaultCenter={initialCenter}
        defaultZoom={3}
        styles={getMapStyles(isLightTheme)}
        gestureHandling={mapActive ? 'greedy' : 'none'}
        disableDefaultUI={true}
        zoomControl={mapActive}
        streetViewControl={false}
        mapTypeControl={false}
        fullscreenControl={false}
        clickableIcons={false}
        onClick={handleMapClick}
        style={{ width: '100%', height: '100%' }}
      >
        <MapBoundsHandler hotels={hotelsWithCoords} />

        {apiIsLoaded && hotelsWithCoords.map(hotel => (
          <Marker
            key={hotel.id}
            position={{ lat: hotel.latitude!, lng: hotel.longitude! }}
            title={hotel.name}
            icon={getMarkerIcon(selectedHotel?.id === hotel.id)}
            onClick={() => handleMarkerClick(hotel)}
            zIndex={selectedHotel?.id === hotel.id ? 1000 : 1}
          />
        ))}
      </GoogleMap>

      {/* Selected Hotel Info Card */}
      {selectedHotel && (
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 w-80 max-w-[calc(100%-2rem)] bg-theme-bg border border-theme-border shadow-xl cursor-pointer hover:border-theme-text transition-colors z-10"
          onClick={handleInfoCardClick}
        >
          <div className="flex h-full">
            {/* Image */}
            <div className="w-28 flex-shrink-0 self-stretch">
              {selectedHotel.imageUrl ? (
                <OptimizedImage
                  src={selectedHotel.imageUrl}
                  alt={selectedHotel.name}
                  className="w-full h-full object-cover"
                  quality={80}
                />
              ) : (
                <div className="w-full h-full bg-theme-bg-secondary" />
              )}
            </div>
            {/* Content */}
            <div className="flex-1 p-3 min-w-0 flex flex-col justify-center">
              <h3 className={`${themeClasses.text.body} font-medium text-theme-text truncate`}>
                {selectedHotel.name}
              </h3>
              {formatLocation(selectedHotel) && (
                <p className={`${themeClasses.text.mutedSmall} text-theme-text-muted truncate mt-1`}>
                  {formatLocation(selectedHotel)}
                </p>
              )}
              <p className={`${themeClasses.text.mutedSmall} text-theme-accent mt-2`}>
                View Details →
              </p>
            </div>
          </div>
        </div>
      )}

      {/* No hotels message */}
      {hotels.length > 0 && hotelsWithCoords.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-theme-bg/80 z-20">
          <p className={themeClasses.text.muted}>No properties with location data</p>
        </div>
      )}
    </div>
  );
}

export default HotelsMap;
